/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState } from "react";
import { IonFooter, IonSpinner, IonTextarea } from "@ionic/react";
import { ICommentInput } from "@interfaces/Components";
import { Button_Onboarding_Tick, Button_Send } from "@assets/images";
import { StringUtils } from "parafolio-components";
import { Capacitor } from "@capacitor/core";
import { Keyboard, KeyboardInfo } from "@capacitor/keyboard";
import classNames from "classnames";

const CommentInput: React.FC<ICommentInput> = (props) => {
  const inputRef = useRef<HTMLIonTextareaElement>(null);

  const [comment, setComment] = useState<string>(props.initialComment ?? "");
  const [sendingComment, setSendingComment] = useState<boolean>(false);

  const [keyboardActive, setKeyboardActive] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [keyboardHeight, setKeyboardHeight] = useState<number>(0);

  useEffect(() => {
    if (Capacitor.getPlatform() === "ios") {
      Keyboard.addListener("keyboardWillShow", (info: KeyboardInfo) => {
        setKeyboardActive(true);
        setKeyboardHeight(info.keyboardHeight);
      });

      Keyboard.addListener("keyboardWillHide", () => {
        setKeyboardActive(false);
      });
    }
  }, []);

  useEffect(() => {
    setComment(props.initialComment ?? "");
    // Focus the input when editing a comment
    if (props.initialComment && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [props.initialComment]);

  const inputChanged = (event: any): void => {
    const value = event.target.value;

    setComment(`${value}`);
  };

  const sendComment = async (): Promise<void> => {
    setSendingComment(true);

    try {
      await props.sendComment(comment);

      setSendingComment(false);
      setComment("");
      inputRef.current?.focus();
    } catch (error) {
      console.error(error);
      setSendingComment(false);
    }
  };

  const editComment = async (): Promise<void> => {
    setSendingComment(true);

    try {
      await props.editComment(comment);

      setSendingComment(false);
      props.resetEditingComment();
      setComment("");
    } catch (error) {
      console.error(error);
    }
  };

  const handleKeyboardEvent = async (event: React.KeyboardEvent<HTMLIonTextareaElement>): Promise<void> => {
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      if (!sendDisabled()) {
        if (props.editingComment) {
          await editComment();
        } else {
          await sendComment();
        }
      }
    }
  };

  const sendCommentPressed = async (): Promise<void> => {
    if (props.editingComment) {
      await editComment();
    } else {
      await sendComment();
    }
  };

  const sendDisabled = (): boolean => {
    const disabled = sendingComment || StringUtils.checkStringIsEmptyAfterTrimming(comment);

    if (props.editingComment) {
      const commentsMatch = comment === props.initialComment;

      return disabled || commentsMatch;
    }

    return disabled;
  };

  return (
    <IonFooter
      className={`flex justify-center flex-col bg-grey-00 shadow-comment-input ${keyboardActive ? "pb-0" : "pb-safe-area-footer"}`}
    >
      {props.commentsDisabled && (
        <div className="flex justify-center">
          <div className="mt-[12px] mb-[4px] rounded-[6px] bg-orange-20 p-[8px] max-w-app-column ml-safe-area-l mr-safe-area-r">
            <div className="text-acc-12px font-medium leading-[1.33] tracking-[-0.1px] text-grey-90">
              {"You can no longer send new comments because your progress check is approved."}
            </div>
          </div>
        </div>
      )}
      <div className="flex flex-row justify-center">
        <div className="flex flex-row items-center min-h-[50px] py-[7px] w-full max-w-app-column pl-comment-input-l pr-comment-input-r">
          <div className="bg-white w-full rounded-[18px] border-solid border-[0.5px] border-grey-60 mr-[12px] overflow-hidden">
            <IonTextarea
              ref={inputRef}
              disabled={props.commentsDisabled}
              autoGrow
              className="!min-h-[34.5px] !py-0 !max-h-[144px] px-[12px] overflow-y-auto text-grey-90"
              placeholder="Add a comment..."
              id="preceptor-comment-input"
              spellcheck
              mode="ios"
              value={comment}
              onIonInput={inputChanged}
              onKeyDown={handleKeyboardEvent}
              rows={1}
            />
          </div>
          <button
            className={`flex shrink-0 w-[37.5px] h-[37.5px] bg-cta-blue rounded-[50px] justify-center items-center overflow-hidden self-end ${
              sendDisabled() ? "opacity-40" : "opacity-100"
            }`}
            onClick={() => sendCommentPressed()}
            disabled={sendDisabled()}
          >
            {sendingComment ? (
              <IonSpinner class="w-[20px] h-[20px] text-white" />
            ) : (
              <img
                className={classNames("!size-11 max-h-full max-w-full img-no-select", {
                  "!size-6": props.editingComment,
                })}
                src={props.editingComment ? Button_Onboarding_Tick : Button_Send}
              />
            )}
          </button>
        </div>
      </div>
    </IonFooter>
  );
};

export default CommentInput;
